import React, { useEffect, useState, useCallback } from "react";
import { Skull, Plus, Trash2, Building2, Truck } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner } from "@/components/Kit";

export default function Watchlist() {
  const { orgId, org } = useOrg();
  const { can } = useAuth();
  const canManage = can("monitor.watchers", "manage");
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", domain: "", kind: "supplier" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!orgId) return;
    http.get("/monitoring/watchlist", { params: { org_id: orgId } }).then((r) => setRows(r.data));
  }, [orgId]);
  useEffect(() => { setRows(null); }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!f.name && !f.domain) { toast.error("Provide a name or a domain"); return; }
    setBusy(true);
    try {
      await http.post("/monitoring/watchlist", { org_id: orgId, ...f });
      setF({ name: "", domain: "", kind: "supplier" });
      toast.success("Added to watchlist");
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    try { await http.delete(`/monitoring/watchlist/${r.id}`, { params: { org_id: orgId } }); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (rows === null) return <div className="p-10 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-6 max-w-[1200px] mx-auto fade-up" data-testid="watchlist-view">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#FFF5EB" }}><Skull className="w-5 h-5 text-[#A5300F]" /></div>
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Ransomware Watchlist</h1>
          <p className="text-sm text-[#8A8175]">{org?.name} · get alerted when your org or a key supplier is named on an extortion leak site.</p>
        </div>
      </div>

      {canManage && (
        <Card className="mt-6 p-4">
          <form onSubmit={add} className="flex flex-wrap items-end gap-3" data-testid="watchlist-form">
            <div className="flex-1 min-w-[160px]">
              <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Name</label>
              <input data-testid="watchlist-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Acme Logistics"
                className="w-full mt-1 border border-[#E7D9C6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Domain</label>
              <input data-testid="watchlist-domain" value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="acme.com"
                className="w-full mt-1 border border-[#E7D9C6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Type</label>
              <select data-testid="watchlist-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
                className="block mt-1 border border-[#E7D9C6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]">
                <option value="self">Own organisation</option>
                <option value="supplier">Supplier / vendor</option>
              </select>
            </div>
            <button type="submit" disabled={busy} data-testid="watchlist-add" className="bg-[#A5300F] text-white font-semibold px-4 py-2 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add
            </button>
          </form>
        </Card>
      )}

      <Card className="mt-6 overflow-hidden">
        <table className="w-full text-sm" data-testid="watchlist-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Type</th><th>Name</th><th>Domain</th><th>Added</th>{canManage && <th className="text-right pr-4"></th>}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`watchlist-row-${r.id}`}>
                <td className="py-2.5 px-4">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: r.kind === "self" ? "#A5300F" : "#5C554C" }}>
                    {r.kind === "self" ? <Building2 className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
                    {r.kind === "self" ? "Own org" : "Supplier"}
                  </span>
                </td>
                <td className="font-semibold text-[#323232]">{r.name || <span className="text-[#B5AC9E]">—</span>}</td>
                <td className="font-mono text-xs text-[#5C554C]">{r.domain || <span className="text-[#B5AC9E]">—</span>}</td>
                <td className="text-xs text-[#8A8175]">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—"}</td>
                {canManage && <td className="text-right pr-4"><button onClick={() => remove(r)} data-testid={`watchlist-del-${r.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]"><Trash2 className="w-4 h-4" /></button></td>}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canManage ? 5 : 4} className="py-10 text-center text-[#B5AC9E]">Watchlist empty — your organisation name is monitored automatically. Add suppliers to widen coverage.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
